import _ from "lodash";
import moment from "moment";
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import Cookies from "universal-cookie";
import appHistory from "../../app/appHistory.js";
import { getUserSelectedAccountCode, getUserSelectedAccountType, getUserUidSelector } from "../../app/appSelectors.js";
import { failurePut } from "../../app/failurePut.js";
import { getRequest, postRequest } from "../../app/request.js";
import { ORDER_TYPES } from "../../components/OrderTypeSelector/orderTypeSelectorConstants.js";
import parseAccountName from "../../utils/parseAccountName";
import Asset from "../Asset/Asset";
import { logoutAction } from "../Login/loginActions.js";
import { openNotificationAction } from "../Notifications/notificationsActions";
import LiteTradeNotificationBasicInfo from "./components/LiteTradeNotification/LiteTradeNotificationBasicInfo";
import LiteTradeNotificationCloseButton from "./components/LiteTradeNotification/LiteTradeNotificationCloseButton";
import {
    LITE_TRADE_NOTIFICATION_TYPES,
    MAP_LITE_TRADE_ORDER_STATUS_TO_ORDER_NOTIFICATION_TYPE
} from "./components/LiteTradeNotification/liteTradeNotificationConstants";
import LiteTradeNotificationPositionClosed
    from "./components/LiteTradeNotification/LiteTradeNotificationPositionClosed";
import LiteTradeNotificationProtectionOrderInfo
    from "./components/LiteTradeNotification/LiteTradeNotificationProtectionOrderInfo";
import {
    cancelLiteTradeOrderFailure,
    cancelLiteTradeOrderSuccess,
    closeLiteTradePositionFailure,
    closeLiteTradePositionSuccess,
    createLiteTradeOrderFailure,
    createLiteTradeOrderSuccess,
    getLiteTradeAccountStatPLFailure,
    getLiteTradeAccountStatPLSuccess,
    getLiteTradeAssetsFailure,
    getLiteTradeAssetsSuccess,
    initLiteTradeFailure,
    initLiteTradeSuccess,
    loginLiteTradeAction,
    loginLiteTradeFailure,
    loginLiteTradeSuccess,
    logoutLiteTradeFailure,
    logoutLiteTradeSuccess,
    openLiteTradePositionFailure,
    openLiteTradePositionSuccess,
    pingLiteTradeAction,
    pingLiteTradeFailure,
    pingLiteTradeSuccess,
    updateLiteTradeOrderFailure,
    updateLiteTradeOrderSuccess,
    updateLiteTradePositionFailure,
    updateLiteTradePositionSuccess
} from "./liteTradeActions.js";
import {
    CANCEL_LITE_TRADE_ORDER_ACTION,
    CLOSE_LITE_TRADE_POSITION_ACTION,
    CREATE_LITE_TRADE_ORDER_ACTION,
    GET_LITE_TRADE_ACCOUNT_STAT_PL_ACTION,
    GET_LITE_TRADE_ASSETS_ACTION,
    INIT_LITE_TRADE_ACTION,
    LITE_TRADE_ORDER_STATUSES,
    LITE_TRADE_POSITION_EFFECTS,
    LITE_TRADE_POSITION_SIDE,
    LOGIN_LITE_TRADE_ACTION,
    LOGOUT_LITE_TRADE_ACTION,
    OPEN_LITE_TRADE_POSITION_ACTION,
    PING_LITE_TRADE_ACTION,
    PROCESS_LITE_TRADE_ORDER_NOTIFICATION,
    PROCESS_LITE_TRADE_POSITION_NOTIFICATION,
    PROCESS_LITE_TRADE_REJECTED_ORDER_NOTIFICATION,
    UPDATE_LITE_TRADE_ORDER_ACTION,
    UPDATE_LITE_TRADE_POSITION_ACTION,
} from "./liteTradeConstants.js";
import {
    deleteLiteTradeRequest,
    getLiteTradeRequest,
    postLiteTradeRequest,
    putLiteTradeRequest
} from "./liteTradeRequest.js";
import { getLiteTradeAssets, makeSelectorGetLiteTradeAccountOrdersByAccountCode } from "./liteTradeSelectors";

const browserCookies = new Cookies();

export function* initLiteTradeSaga() {
    try {
        const data = yield call(getRequest, '/user/lite-trade/init');
        localStorage.setItem('apiTrade', data.tradeUrl);
        yield put(initLiteTradeSuccess(data));
        yield put(pingLiteTradeAction());
    } catch (e) {
        yield call(failurePut, e, initLiteTradeFailure);
    }
}

export function* pingLiteTradeSaga() {
    try {
        const data = yield call(postLiteTradeRequest, '/ping', null, null, {
            timeout: 5000,
        });

        const pin = browserCookies.get('pin');
        browserCookies.set('pinOk', pin, { path: '/', maxAge: 60 * 5 });

        yield put(pingLiteTradeSuccess(data));
    } catch (e) {
        if (e.response?.status === 401) {
            yield put(loginLiteTradeAction());
        } else {
            yield call(failurePut, e, pingLiteTradeFailure);
        }
    }
}

export function* loginLiteTradeSaga() {
    try {
        const data = yield call(postRequest, '/user/lite-trade/login');
        if (data?.sessionToken) {
            localStorage.setItem('sessionToken', data.sessionToken);
        }
        yield put(loginLiteTradeSuccess(data));
    } catch (e) {
        yield call(failurePut, e, loginLiteTradeFailure);
        yield put(logoutAction());
    }
}

export function* logoutLiteTradeSaga() {
    try {
        // const data = yield call(postLiteTradeRequest, '/logout');
        localStorage.removeItem('selectedAccount');
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('credentialId');
        browserCookies.remove('credential_id');
        yield put(logoutAction());
        yield put(logoutLiteTradeSuccess());
    } catch (e) {
        yield call(failurePut, e, logoutLiteTradeFailure);
    }
}

export function* getLiteTradeAssetsSaga() {
    try {
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const data = yield call(getLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/instruments/query`);
        const data2 = yield call(getLiteTradeRequest, `/instruments/query`);
        yield put(getLiteTradeAssetsSuccess(data.instrumentDetails.map(item => ({
            ...item,
            info: data2.instruments.find(item2 => item2.symbol === item.symbol),
        }))));
    } catch (e) {
        yield call(failurePut, e, getLiteTradeAssetsFailure);
    }
}

export function* getOrderCode() {
    const uid = yield select(getUserUidSelector);
    const accountType = yield select(getUserSelectedAccountType);
    return `source_app-${accountType}_${uid}-${moment().unix()}`;
}

/**
 * @deprecated use openLiteTradePositionSaga/closeLiteTradePositionSaga
 */
export function* createLiteTradeOrderSaga({ payload }) {
    try {
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const orderCode = yield call(getOrderCode);
        const data = yield call(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, {
            ...payload,
            orderCode,
        });
        yield put(createLiteTradeOrderSuccess(data));
        if (payload.positionEffect === 'OPEN') {
            appHistory.goBack();
        }
    } catch (e) {
        yield call(failurePut, e, createLiteTradeOrderFailure);
    }
}

export function* openLiteTradePositionSaga({ payload }) {
    try {
        const orders = [];
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const orderCode = yield call(getOrderCode);
        const params = {
            instrument: payload.symbol,
            orderCode,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.OPEN,
            quantity: payload.quantity,
            side: payload.side.toLowerCase() === 'buy' ? LITE_TRADE_POSITION_SIDE.BUY: LITE_TRADE_POSITION_SIDE.SELL,
            tif: "GTC",
            type: payload.orderType,
        };
        switch (payload.orderType) {
            case ORDER_TYPES.LIMIT:
                params.limitPrice = payload.limitPrice;
                break;
            case ORDER_TYPES.STOP:
                params.stopPrice = payload.stopPrice;
                break;
            default:
        }
        orders.push(params);

        if (payload.takeProfitActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: `${orderCode}-take`,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY:
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: "GTC",
                type: ORDER_TYPES.LIMIT,
                limitPrice: payload.takeProfit,
            });
        }

        if (payload.stopLossActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: `${orderCode}-stop`,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY:
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: "GTC",
                type: ORDER_TYPES.STOP,
                stopPrice: payload.stopLoss,
            });
        }

        let body;
        if (orders.length > 1) {
            body = {
                orders,
                contingencyType: "IF-THEN"
            }
        } else {
            body = orders[0];
        }
        const data = yield call(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, body);
        yield put(openLiteTradePositionSuccess(data));
        if (params.positionEffect === LITE_TRADE_POSITION_EFFECTS.OPEN) {
            appHistory.goBack();
        }
    } catch (e) {
        yield call(failurePut, e, openLiteTradePositionFailure);
    }
}

export function* closeLiteTradePositionSaga({ payload }) {
    try {
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const orderCode = yield call(getOrderCode);
        const params = {
            instrument: payload.symbol,
            orderCode,
            positionCode: payload.positionCode,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
            quantity: payload.quantity,
            side: payload.side,
            tif: "GTC",
            type: ORDER_TYPES.MARKET,
        };

        const data = yield call(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, params);
        yield put(closeLiteTradePositionSuccess(data));
        payload.onSuccessCallback && payload.onSuccessCallback();
    } catch (e) {
        yield call(failurePut, e, closeLiteTradePositionFailure);
    }
}

export function* cancelLiteTradeOrderSaga({ payload }) {
    try {
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const data = yield call(deleteLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders/${encodeURIComponent(payload.orderCode)}`);
        yield put(cancelLiteTradeOrderSuccess(data));
    } catch (e) {
        yield call(failurePut, e, cancelLiteTradeOrderFailure);
    }
}

export function* updateLiteTradeOrderSaga({ payload }) {
    try {
        const orders = [];
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const params = {
            instrument: payload.symbol,
            orderCode: payload.orderCode,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.OPEN,
            quantity: payload.quantity,
            side: payload.side.toLowerCase() === 'buy' ? LITE_TRADE_POSITION_SIDE.BUY: LITE_TRADE_POSITION_SIDE.SELL,
            tif: "GTC",
        };
        switch (payload.orderType) {
            case ORDER_TYPES.LIMIT:
                params.limitPrice = payload.limitPrice;
                break;
            case ORDER_TYPES.STOP:
                params.stopPrice = payload.stopPrice;
                break;
            default:
        }
        orders.push(params);

        if (payload.takeProfitActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: payload.initialOrderTakeProfit?.orderCode || `${payload.orderCode}-take-${moment().unix()}`,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY:
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: "GTC",
                limitPrice: payload.takeProfit,
            });
        }

        if (payload.stopLossActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: payload.initialOrderStopLoss?.orderCode || `${payload.orderCode}-stop-${moment().unix()}`,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY:
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: "GTC",
                type: ORDER_TYPES.STOP,
                stopPrice: payload.stopLoss,
            });
        }

        let body;
        if (orders.length > 1) {
            body = {
                orders,
                contingencyType: "IF-THEN"
            }
        } else {
            body = orders[0];
        }
        const data = yield call(putLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, body);
        yield put(updateLiteTradeOrderSuccess(data));
        appHistory.goBack();
    } catch (e) {
        yield call(failurePut, e, updateLiteTradeOrderFailure);
    }
}

export function* updateLiteTradePositionSaga({ payload }) {
    try {
        const {
            initialOrderStopLoss,
            initialOrderTakeProfit,
            initialQuantity,
            positionCode,
            quantity,
            side,
            stopLoss,
            stopLossActive,
            symbol,
            takeProfit,
            takeProfitActive,
        } = payload;

        const orderSide = side.toLowerCase() === 'sell' ?
            LITE_TRADE_POSITION_SIDE.BUY:
            LITE_TRADE_POSITION_SIDE.SELL;
        const positionQuantityToClose = initialQuantity - quantity;
        if (positionQuantityToClose) {
            yield call(closeLiteTradePositionSaga, {
                payload: {
                    positionCode,
                    quantity: positionQuantityToClose,
                    side: orderSide,
                    symbol,
                }
            });
        }

        const newOrderCode = yield call(getOrderCode);
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const ordersToCreate = [];
        const ordersToUpdate = [];
        const ordersToDelete = [];
        const orderCommonParams = {
            positionCode,
            instrument: symbol,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
            side: orderSide,
            tif: "GTC",
        }

        let newOrderTakeProfit = {
            ...orderCommonParams,
            orderCode: initialOrderTakeProfit?.orderCode || `${newOrderCode}-take`,
            limitPrice: takeProfit,
            type: ORDER_TYPES.LIMIT,
        };

        let newOrderStopLoss = {
            ...orderCommonParams,
            orderCode: initialOrderStopLoss?.orderCode || `${newOrderCode}-stop`,
            stopPrice: stopLoss,
            type: ORDER_TYPES.STOP,
        };

        [
            {
                initialOrder: initialOrderTakeProfit,
                newOrder: newOrderTakeProfit,
                isOrderActive: takeProfitActive,
                orderPrice: takeProfit
            },
            {
                initialOrder: initialOrderStopLoss,
                newOrder: newOrderStopLoss,
                isOrderActive: stopLossActive,
                orderPrice: stopLoss
            },
        ].forEach(({
                       initialOrder, newOrder, isOrderActive, orderPrice,
                   }) => {
            if (isOrderActive) {
                if (!initialOrder) {
                    ordersToCreate.push(newOrder);
                } else if (initialOrder.legs[0].price !== orderPrice) {
                    ordersToUpdate.push(_.omit(newOrder, ['type']));
                }
            } else if (initialOrder) {
                ordersToDelete.push(newOrder);
            }
        });

        yield all(ordersToDelete.map(orderBody =>
            call(deleteLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders/${encodeURIComponent(orderBody.orderCode)}`))
        );
        yield all(ordersToUpdate.map(orderBody =>
            call(putLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, orderBody))
        );
        yield all(ordersToCreate.map(orderBody =>
            call(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, orderBody))
        );

        yield put(updateLiteTradePositionSuccess());
        appHistory.goBack();
    } catch (e) {
        yield call(failurePut, e, updateLiteTradePositionFailure);
    }
}

export function* loadLiteTradeMarketData({ payload }) {
    const { accountCode, symbols } = payload;
    const params = {
        account: `default:${accountCode}`,
        symbols: symbols,
        eventTypes: [
            {
                type: "Quote",
                format: "COMPACT",
            }
        ]
    };
    try {
        return yield call(postLiteTradeRequest, '/marketdata', params);
    } catch (e) {
        return e
    }
}

export function* openLiteTradeBasicInfoNotificationSaga({ payload }) {
    const {
        accountCode,
        autoClose = true,
        description,
        notificationChildren,
        NotificationComponent = LiteTradeNotificationBasicInfo,
        notificationOptions = {},
        notificationStatus,
        notificationType,
        order,
        pl,
        price,
        quantity,
    } = payload;
    const assets = yield select(getLiteTradeAssets);
    const asset = _.find(assets, { symbol: payload.order.instrument });
    const assetInstance = new Asset(asset, { assets, selectedAccountCode: accountCode });
    yield call(assetInstance.isReady.bind(assetInstance));
    const lotsAmount = quantity / assetInstance.getLotSize();
    const margin = assetInstance.lotsToAmount(lotsAmount, price);
    const accountType = parseAccountName(order.account).type;

    yield put(openNotificationAction({
        content: <NotificationComponent
            accountType={accountType}
            assetInstance={assetInstance}
            description={description}
            lotsAmount={lotsAmount}
            margin={margin}
            order={order}
            pl={pl}
            price={price}
            status={notificationStatus}
            symbol={order.instrument}
            type={notificationType}
        >{notificationChildren}</NotificationComponent>,
        options: {
            autoClose,
            closeButton: LiteTradeNotificationCloseButton,
            ...notificationOptions,
        }
    }))
}

export function* processLiteTradePositionNotificationSaga({ payload }) {
    const { portfolio, order } = payload;
    const positionEffect = order?.legs[0]?.positionEffect;
    const positionCode = order?.legs[0]?.positionCode;
    const position = _.find(portfolio.positions, { positionCode })
    const notificationType = positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
        ? position.quantity
            ? LITE_TRADE_NOTIFICATION_TYPES.POSITION_PARTIALLY_CLOSED
            : LITE_TRADE_NOTIFICATION_TYPES.POSITION_CLOSED
        : LITE_TRADE_NOTIFICATION_TYPES.POSITION_OPENED;
    const accountCode = parseAccountName(order.account).accountCode;
    const execution = order.executions.find(execution => execution.status === order.status);
    const price = execution?.lastPrice;
    const quantity = execution?.lastQuantity;
    const pl = order.cashTransactions[0]?.value;
    const isBottomNotification = [
        LITE_TRADE_NOTIFICATION_TYPES.POSITION_CLOSED,
        LITE_TRADE_NOTIFICATION_TYPES.POSITION_PARTIALLY_CLOSED,
    ].includes(notificationType);
    const NotificationComponent = isBottomNotification ? LiteTradeNotificationPositionClosed: LiteTradeNotificationBasicInfo;
    const notificationOptions = isBottomNotification
        ? {
            className: 'background-surface',
            limit: 1,
            position: "bottom-center",
        }
        : {};

    yield call(openLiteTradeBasicInfoNotificationSaga, {
        payload: {
            NotificationComponent,
            accountCode,
            notificationOptions,
            notificationType,
            order,
            pl,
            price,
            quantity,
        }
    })
}

export function* processLiteTradeOrderNotificationSaga({ payload }) {
    const { order } = payload;
    const accountCode = parseAccountName(order.account).accountCode;
    // check initial get orders
    const currentAccountOrders = yield select(makeSelectorGetLiteTradeAccountOrdersByAccountCode(accountCode));
    if (!currentAccountOrders) {
        return
    }

    const price = order?.legs[0]?.price;
    const quantity = order?.legs[0]?.quantity;
    const foundOrder = _.find(currentAccountOrders, { orderId: order.orderId });
    let isOrderChanged = false;
    if (order.status === LITE_TRADE_ORDER_STATUSES.WORKING && foundOrder) {
        const foundOrderQuantity = foundOrder.legs[0].quantity;
        const foundOrderPrice = foundOrder.legs[0].price;
        isOrderChanged = quantity !== foundOrderQuantity || price !== foundOrderPrice;
        if (!isOrderChanged) {
            return;
        }
    }

    const notificationType = isOrderChanged
        ? LITE_TRADE_NOTIFICATION_TYPES.ORDER_CHANGED
        : MAP_LITE_TRADE_ORDER_STATUS_TO_ORDER_NOTIFICATION_TYPE[order.status];

    const NotificationComponent = order.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
        ? LiteTradeNotificationProtectionOrderInfo
        : undefined;

    yield call(openLiteTradeBasicInfoNotificationSaga, {
        payload: {
            NotificationComponent,
            notificationType,
            accountCode,
            price,
            quantity,
            order
        }
    })
}

export function* processLiteTradeRejectedOrderNotificationSaga({ payload }) {
    const { description, notificationChildren, notificationStatus, order, autoClose = true } = payload;
    const accountCode = parseAccountName(order.account).accountCode;
    const quantity = order?.legs[0]?.quantity;
    const notificationType = LITE_TRADE_NOTIFICATION_TYPES.ORDER_REJECTED;
    let price;
    if (order.type === ORDER_TYPES.MARKET) {
        const marketData = yield call(loadLiteTradeMarketData, {
            payload: {
                accountCode,
                symbols: [order.instrument]
            }
        })
        const event = marketData?.events[0] || {};
        price = order.side === LITE_TRADE_POSITION_SIDE.BUY ? event.ask: event.bid;
    } else {
        price = order?.legs[0]?.price;
    }

    const NotificationComponent = order.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
        ? LiteTradeNotificationProtectionOrderInfo
        : undefined;

    yield call(openLiteTradeBasicInfoNotificationSaga, {
        payload: {
            accountCode,
            autoClose,
            description,
            notificationChildren,
            NotificationComponent,
            notificationStatus,
            notificationType,
            order,
            price,
            quantity,
        }
    })
}


export function* getLiteTradeAccountStatPLSaga({ payload }) {
    try {
        const selectedAccount = yield select(getUserSelectedAccountCode);
        const data = yield call(getRequest, `/account/${encodeURIComponent(selectedAccount)}/pl/${payload}`);
        yield put(getLiteTradeAccountStatPLSuccess(data));
    } catch (e) {
        yield call(failurePut, e, getLiteTradeAccountStatPLFailure);
    }
}

export function* liteTradeSagas() {
    yield takeEvery(INIT_LITE_TRADE_ACTION, initLiteTradeSaga);
    yield takeEvery(PING_LITE_TRADE_ACTION, pingLiteTradeSaga);
    yield takeEvery(LOGIN_LITE_TRADE_ACTION, loginLiteTradeSaga);
    yield takeEvery(LOGOUT_LITE_TRADE_ACTION, logoutLiteTradeSaga);
    yield takeEvery(GET_LITE_TRADE_ASSETS_ACTION, getLiteTradeAssetsSaga);
    yield takeEvery(CREATE_LITE_TRADE_ORDER_ACTION, createLiteTradeOrderSaga);
    yield takeEvery(OPEN_LITE_TRADE_POSITION_ACTION, openLiteTradePositionSaga);
    yield takeEvery(CLOSE_LITE_TRADE_POSITION_ACTION, closeLiteTradePositionSaga);
    yield takeEvery(CANCEL_LITE_TRADE_ORDER_ACTION, cancelLiteTradeOrderSaga);
    yield takeEvery(PROCESS_LITE_TRADE_POSITION_NOTIFICATION, processLiteTradePositionNotificationSaga);
    yield takeEvery(PROCESS_LITE_TRADE_ORDER_NOTIFICATION, processLiteTradeOrderNotificationSaga);
    yield takeEvery(PROCESS_LITE_TRADE_REJECTED_ORDER_NOTIFICATION, processLiteTradeRejectedOrderNotificationSaga);
    yield takeEvery(UPDATE_LITE_TRADE_ORDER_ACTION, updateLiteTradeOrderSaga);
    yield takeEvery(UPDATE_LITE_TRADE_POSITION_ACTION, updateLiteTradePositionSaga);
    yield takeEvery(GET_LITE_TRADE_ACCOUNT_STAT_PL_ACTION, getLiteTradeAccountStatPLSaga);
}
